import * as XLSX from 'xlsx';
import { UploadedDataset } from '../../services/ai-agent.service';
import { CONC_UNIT, OPC_BIN_COUNT, binRangeLabel } from './particleBinShared';

/** A time slice picked on the contour plot, exported alongside the average. */
export type SliceExport = { timestamp: number; values: (number | null)[] };

/**
 * Per-bin statistics, structurally what both the OPC and APS distribution
 * endpoints return. Declared here rather than imported from either service so
 * this module stays usable by both.
 */
export type ExportBinStats = {
  bin: number;
  min: number | null;
  max: number | null;
  mean: number | null;
  p05: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  p95: number | null;
};

type ExportMeta = {
  serial: number | string;
  startDate: number;
  endDate: number;
  interval?: string;
  boundaries: number[];
  /** Bins in the matrix. Defaults to the OPC-N3's 24. */
  binCount?: number;
  /** Unit of the exported values. Defaults to the OPC-N3's #/ml. */
  unit?: string;
  /** Instrument name used in the filename and the agent prompt. */
  instrument?: string;
  /** Filename stem prefix, e.g. "opc" or "aps". */
  filePrefix?: string;
};

/**
 * Bin count for a given export, defaulting to the OPC-N3's.
 * @param meta - Export context.
 * @returns Number of bins.
 */
function countOf(meta: ExportMeta): number {
  return meta.binCount ?? OPC_BIN_COUNT;
}

/**
 * Value unit for a given export, defaulting to the OPC-N3's.
 * @param meta - Export context.
 * @returns Unit label.
 */
function unitOf(meta: ExportMeta): string {
  return meta.unit ?? CONC_UNIT;
}

/**
 * Column headers for each bin, labelled by its particle-size range.
 * @param boundaries - The bin edges (µm).
 * @param binCount - Number of bins to label.
 * @returns Header labels like "0.35–0.46 µm".
 */
function binHeaders(boundaries: number[], binCount: number): string[] {
  return Array.from({ length: binCount }, (_, b) => `${binRangeLabel(boundaries, b)} µm`);
}

/**
 * Excel keeps a blank cell distinct from 0, which matters here: null means the
 * device reported nothing for that bucket, not that it measured zero.
 * @param v - Concentration or null.
 * @returns The number, or an empty string for a gap.
 */
function cell(v: number | null | undefined): number | string {
  return v === null || v === undefined || !Number.isFinite(v) ? '' : v;
}

/**
 * Build the metadata sheet shared by both exports so a downloaded file is
 * self-describing (which device, window, resolution and bin edges produced it).
 * @param meta - Export context.
 * @returns A worksheet.
 */
function metaSheet(meta: ExportMeta): XLSX.WorkSheet {
  const rows: (string | number)[][] = [
    ['Device serial', meta.serial],
    ['Range start', new Date(meta.startDate).toISOString()],
    ['Range end', new Date(meta.endDate).toISOString()],
    ['Range start (local)', new Date(meta.startDate).toLocaleString()],
    ['Range end (local)', new Date(meta.endDate).toLocaleString()],
    ...(meta.interval ? [['Interval', meta.interval] as (string | number)[]] : []),
    ['Value unit', unitOf(meta)],
    ['Bin count', countOf(meta)],
    ...(meta.instrument ? [['Instrument', meta.instrument] as (string | number)[]] : []),
    ['Exported at', new Date().toISOString()],
    [],
    ['Bin', 'Lower edge (µm)', 'Upper edge (µm)'],
    ...Array.from({ length: countOf(meta) }, (_, b) => [
      b,
      meta.boundaries[b],
      meta.boundaries[b + 1]
    ])
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

/**
 * Filename stem tying the export to its device and window.
 * @param prefix - Leading label.
 * @param meta - Export context.
 * @returns Filename without extension.
 */
function fileStem(prefix: string, meta: ExportMeta): string {
  /**
   * Date part of a timestamp, for the filename.
   * @param ms - Epoch millis.
   * @returns "YYYY-MM-DD".
   */
  const day = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
  return `${prefix}-${meta.serial}-${day(meta.startDate)}-to-${day(meta.endDate)}`;
}

/**
 * Download the size distribution: the window statistics as rows, plus one row
 * for each time slice picked on the contour plot, all sharing the bin columns.
 * @param meta - Export context.
 * @param stats - Per-bin window statistics.
 * @param slices - Time slices picked on the contour plot.
 */
export function downloadSizeDistribution(
  meta: ExportMeta,
  stats: ExportBinStats[],
  slices: SliceExport[]
): void {
  const binCount = countOf(meta);
  const header = ['Series', 'Timestamp', ...binHeaders(meta.boundaries, binCount)];
  /**
   * One statistics row across all bins.
   * @param label - Row label.
   * @param pick - Field to read from a bin's stats.
   * @returns The row.
   */
  const statRow = (
    label: string,
    pick: (s: ExportBinStats | undefined) => number | null
  ): (string | number)[] => [
    label,
    '',
    ...Array.from({ length: binCount }, (_, b) => cell(pick(stats.find((s) => s.bin === b))))
  ];

  const rows: (string | number)[][] = [
    statRow('Mean (window)', (s) => s?.mean ?? null),
    statRow('Median p50', (s) => s?.p50 ?? null),
    statRow('p05', (s) => s?.p05 ?? null),
    statRow('p25', (s) => s?.p25 ?? null),
    statRow('p75', (s) => s?.p75 ?? null),
    statRow('p95', (s) => s?.p95 ?? null),
    statRow('Min', (s) => s?.min ?? null),
    statRow('Max', (s) => s?.max ?? null),
    ...slices.map((s, i) => [
      slices.length > 1 ? `Selected time ${i + 1}` : 'Selected time',
      new Date(s.timestamp).toLocaleString(),
      ...Array.from({ length: binCount }, (_, b) => cell(s.values[b]))
    ])
  ];

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    book,
    XLSX.utils.aoa_to_sheet([header, ...rows]),
    'Size Distribution'
  );
  XLSX.utils.book_append_sheet(book, metaSheet(meta), 'Metadata');
  XLSX.writeFile(book, `${fileStem(`${meta.filePrefix ?? 'opc'}-size-distribution`, meta)}.xlsx`);
}

/**
 * Shape the plotted matrix as an uploaded dataset, which the agent can already
 * read through its `read_uploaded_dataset` tool — no new server-side tool
 * needed to reason about bin data.
 * @param meta - Export context.
 * @param timestamps - Bucket start times, epoch millis.
 * @param values - Concentration matrix indexed values[t][b].
 * @returns Dataset in the agent's upload format.
 */
export function buildBinDataset(
  meta: ExportMeta,
  timestamps: number[],
  values: (number | null)[][]
): UploadedDataset {
  return {
    filename: `${fileStem(`${meta.filePrefix ?? 'opc'}-bins`, meta)}.csv`,
    uploadedAt: Date.now(),
    headers: ['Timestamp', ...binHeaders(meta.boundaries, countOf(meta))],
    rows: timestamps.map((ts, t) => [
      new Date(ts).toISOString(),
      ...Array.from({ length: countOf(meta) }, (_, b) => {
        const v = values[t]?.[b];
        return v === null || v === undefined || !Number.isFinite(v) ? null : v;
      })
    ])
  };
}

/**
 * The question pre-filled in the agent composer alongside the dataset. It
 * spells out the units and the blank-means-gap convention, which the column
 * headers alone do not convey.
 * @param meta - Export context.
 * @returns Prompt text.
 */
export function buildBinPrompt(meta: ExportMeta): string {
  const window = `${new Date(meta.startDate).toLocaleString()} to ${new Date(
    meta.endDate
  ).toLocaleString()}`;
  return [
    `The attached dataset is the ${meta.instrument ?? 'OPC-N3'} particle-size bin matrix for device ${meta.serial}, ${window}`,
    meta.interval ? ` at ${meta.interval} resolution.` : '.',
    ` Each column after the timestamp is a particle-size range in µm and each value is in ${unitOf(meta)};`,
    ' a blank cell means the device reported nothing for that bucket, which is not the same as a measured zero.',
    ' Read the dataset and tell me what the size distribution shows: which size modes dominate,',
    ' how the distribution shifts over time, any events or unusual periods worth a closer look,',
    ' and what would plausibly explain them.'
  ].join('');
}
